import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, FileText, AlertCircle, CheckCircle, Clock, X, ChevronDown, ChevronRight, Edit2, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import apiClient from '../../api/client';

const RFQImportModal = ({ isOpen, onClose, opportunityId, onImportComplete, existingDocumentId }) => {
  const [step, setStep] = useState('upload'); // upload, processing, review, applying, complete
  const [documentId, setDocumentId] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [uploading, setUploading] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Load existing document if provided
  useEffect(() => {
    if (existingDocumentId && isOpen) {
      loadExistingDocument(existingDocumentId);
    } else if (!existingDocumentId && isOpen) {
      // Reset to upload state for new imports
      setStep('upload');
      setDocumentId(null);
      setDocumentData(null);
      setSelectedItems({});
    }
  }, [existingDocumentId, isOpen]);

  const loadExistingDocument = async (docId) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/v1/rfq/documents/${docId}`);
      if (response.data.success) {
        const doc = response.data.data;
        setDocumentId(docId);
        setDocumentData(doc);
        if (doc.status === 'extracted') {
          initializeSelections(doc.extracted_items);
          setStep('review');
        } else if (doc.status === 'processing' || doc.status === 'pending') {
          setStep('processing');
        } else if (doc.status === 'applied') {
          toast({
            title: 'Already Applied',
            description: 'This RFQ document has already been imported.',
          });
          onClose();
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
      toast({
        title: 'Error',
        description: 'Failed to load document',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Poll for extraction status
  useEffect(() => {
    if (documentId && step === 'processing') {
      const interval = setInterval(async () => {
        try {
          const response = await apiClient.get(`/api/v1/rfq/documents/${documentId}`);
          if (response.data.success) {
            const doc = response.data.data;
            if (doc.status === 'extracted') {
              setDocumentData(doc);
              initializeSelections(doc.extracted_items);
              setStep('review');
              clearInterval(interval);
            } else if (doc.status === 'failed') {
              toast({
                title: 'Extraction Failed',
                description: doc.extraction_error || 'Failed to extract data from document',
                variant: 'destructive'
              });
              setStep('upload');
              clearInterval(interval);
            }
          }
        } catch (error) {
          console.error('Error polling document status:', error);
        }
      }, 2000);
      
      setPollInterval(interval);
      return () => clearInterval(interval);
    }
  }, [documentId, step]);

  // Initialize selections based on auto-checked status
  const initializeSelections = (items) => {
    const selections = {};
    items.forEach(item => {
      if (item.item_type === 'part') {
        selections[item.id] = item.auto_checked;
      }
    });
    setSelectedItems(selections);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['pdf', 'xlsx', 'xls', 'docx', 'doc'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast({
        title: 'Invalid File Type',
        description: `Allowed types: ${allowedTypes.join(', ')}`,
        variant: 'destructive'
      });
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Maximum file size is 25MB',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post(
        `/api/v1/rfq/opportunities/${opportunityId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        setDocumentId(response.data.document_id);
        setStep('processing');
        toast({
          title: 'Document Uploaded',
          description: 'AI is analyzing your RFQ document...',
        });
      } else if (response.data.error === 'duplicate') {
        toast({
          title: 'Duplicate Document',
          description: `This document was already uploaded: ${response.data.existing_filename}`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleApplyChanges = async () => {
    setStep('applying');

    const itemsToApply = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([itemId, _]) => {
        const edited = editValues[itemId];
        return {
          item_id: itemId,
          action: edited ? 'edit' : 'accept',
          edited_value: edited || null
        };
      });

    try {
      const response = await apiClient.post(
        `/api/v1/rfq/documents/${documentId}/apply`,
        { items: itemsToApply }
      );

      if (response.data.success) {
        setStep('complete');
        toast({
          title: 'Changes Applied',
          description: `Created ${response.data.summary.parts_created} parts, updated ${response.data.summary.parts_updated} parts`,
        });
        onImportComplete?.(response.data);
      }
    } catch (error) {
      console.error('Apply error:', error);
      toast({
        title: 'Apply Failed',
        description: error.response?.data?.detail || 'Failed to apply changes',
        variant: 'destructive'
      });
      setStep('review');
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const toggleItemExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const selectAll = () => {
    const allSelected = {};
    documentData?.extracted_items?.forEach(item => {
      if (item.item_type === 'part') {
        allSelected[item.id] = true;
      }
    });
    setSelectedItems(allSelected);
  };

  const clearAll = () => {
    setSelectedItems({});
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(Boolean).length;
  };

  const getStatusBadge = (item) => {
    if (item.will_create_new) {
      return <Badge className="bg-green-100 text-green-800 border-green-200">NEW</Badge>;
    }
    if (item.match_type === 'exact') {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">MATCH</Badge>;
    }
    if (item.match_type === 'fuzzy') {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">FUZZY {Math.round((item.match_confidence || 0) * 100)}%</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">UNKNOWN</Badge>;
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatNumber = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Import RFQ Document</h3>
        <p className="text-sm text-gray-600">
          Upload an RFQ document (PDF, Excel, or Word) to extract part information, volumes, and pricing.
        </p>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
          uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        }`}
      >
        {uploading ? (
          <div className="space-y-3">
            <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin" />
            <p className="text-sm text-gray-700">Uploading document...</p>
          </div>
        ) : (
          <>
            <div className="flex justify-center gap-4 mb-4">
              <FileSpreadsheet className="w-12 h-12 text-green-500" />
              <FileText className="w-12 h-12 text-red-500" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Drop your RFQ document here or click to browse
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Supported: PDF, XLSX, XLS, DOCX (Max 25MB)
            </p>
            <input
              type="file"
              className="hidden"
              id="rfq-file-upload"
              accept=".pdf,.xlsx,.xls,.docx,.doc"
              onChange={handleFileUpload}
            />
            <label
              htmlFor="rfq-file-upload"
              className="inline-block px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
            >
              Choose File
            </label>
          </>
        )}
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center py-12">
      <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin" />
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Document</h3>
        <p className="text-sm text-gray-600">
          AI is extracting parts, volumes, and pricing from your RFQ document...
        </p>
        <p className="text-xs text-gray-500 mt-2">This usually takes 10-30 seconds</p>
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const extractedData = documentData?.extracted_data || {};
    const partItems = documentData?.extracted_items?.filter(i => i.item_type === 'part') || [];
    const programInfo = extractedData.program_info || {};
    const documentInfo = extractedData.document_info || {};
    const customerInfo = extractedData.customer_info || {};

    return (
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Document Header */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3">Document Info</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">RFQ Number:</span>
              <p className="font-medium">{documentInfo.rfq_number || '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">Customer:</span>
              <p className="font-medium">{customerInfo.customer_name || '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">Quote Due:</span>
              <p className="font-medium">{documentInfo.quote_due_date || '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">Program:</span>
              <p className="font-medium">{programInfo.program_name || '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">SOP:</span>
              <p className="font-medium">{programInfo.sop_date || '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">EOP:</span>
              <p className="font-medium">{programInfo.eop_date || '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">Duration:</span>
              <p className="font-medium">{programInfo.program_duration_years ? `${programInfo.program_duration_years} years` : '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">Confidence:</span>
              <p className="font-medium">{documentData?.extraction_confidence ? `${Math.round(documentData.extraction_confidence * 100)}%` : '-'}</p>
            </div>
          </div>
        </div>

        {/* Parts Header */}
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">
            Extracted Parts ({partItems.length})
          </h4>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
            <Button variant="outline" size="sm" onClick={clearAll}>Clear</Button>
          </div>
        </div>

        {/* Parts List */}
        <div className="space-y-2">
          {partItems.map((item) => {
            const partData = item.extracted_data || {};
            const isExpanded = expandedItems[item.id];
            const isSelected = selectedItems[item.id];
            // current_values might be a string or an object depending on the API response
            let currentValues = null;
            if (item.current_values) {
              if (typeof item.current_values === 'string') {
                try {
                  currentValues = JSON.parse(item.current_values);
                } catch (e) {
                  currentValues = null;
                }
              } else {
                currentValues = item.current_values;
              }
            }
            // Also check current_values from the API response (already parsed)
            if (!currentValues && item.current_values_parsed) {
              currentValues = item.current_values_parsed;
            }

            return (
              <div
                key={item.id}
                className={`border rounded-lg transition-colors ${
                  isSelected ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200 bg-white'
                }`}
              >
                {/* Part Header */}
                <div className="flex items-center gap-3 p-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleItemSelection(item.id)}
                  />
                  <button
                    onClick={() => toggleItemExpanded(item.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-gray-900">
                        {partData.part_number}
                      </span>
                      {getStatusBadge(item)}
                      <Badge variant="outline" className="text-xs">
                        {Math.round(item.confidence_score * 100)}% conf
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {partData.description || 'No description'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(partData.target_price_usd)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatNumber(partData.annual_volume)}/yr
                    </p>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left font-medium pb-2">Field</th>
                          <th className="text-left font-medium pb-2">Current Value</th>
                          <th className="text-left font-medium pb-2">RFQ Value</th>
                          <th className="text-left font-medium pb-2">Delta</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className="py-2 text-gray-600">Description</td>
                          <td className="py-2">{currentValues?.description || '-'}</td>
                          <td className="py-2 font-medium">{partData.description || '-'}</td>
                          <td className="py-2">
                            {!currentValues?.description && partData.description && (
                              <Badge className="bg-green-100 text-green-700">NEW</Badge>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 text-gray-600">Target Price</td>
                          <td className="py-2">{currentValues?.target_price_cents ? formatCurrency(currentValues.target_price_cents / 100) : '-'}</td>
                          <td className="py-2 font-medium">{formatCurrency(partData.target_price_usd)}</td>
                          <td className="py-2">
                            {!currentValues?.target_price_cents && partData.target_price_usd && (
                              <Badge className="bg-green-100 text-green-700">NEW</Badge>
                            )}
                            {currentValues?.target_price_cents && partData.target_price_usd && (
                              <span className="text-yellow-600">
                                {((partData.target_price_usd - currentValues.target_price_cents / 100) / (currentValues.target_price_cents / 100) * 100).toFixed(1)}%
                              </span>
                            )}
                          </td>
                        </tr>
                        {/* Volume by Year */}
                        {Object.entries(partData.volumes_by_year || {}).map(([year, volume]) => {
                          const currentVol = currentValues?.volumes_by_year?.[year];
                          return (
                            <tr key={year}>
                              <td className="py-2 text-gray-600">{year} Volume</td>
                              <td className="py-2">{currentVol ? formatNumber(currentVol) : '-'}</td>
                              <td className="py-2 font-medium">{formatNumber(volume)}</td>
                              <td className="py-2">
                                {!currentVol && volume && (
                                  <Badge className="bg-green-100 text-green-700">NEW ✓</Badge>
                                )}
                                {currentVol && currentVol !== volume && (
                                  <span className="text-yellow-600">
                                    {((volume - currentVol) / currentVol * 100).toFixed(1)}%
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Warning for existing values */}
                    {item.match_type !== 'none' && currentValues?.target_price_cents && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                        <p className="text-xs text-yellow-800">
                          This part has existing values. Only empty fields will be updated unless you explicitly enable overwriting.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
          <div className="space-y-1 text-sm">
            <p className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              {partItems.filter(i => i.will_create_new && selectedItems[i.id]).length} new parts will be created
            </p>
            <p className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              {partItems.filter(i => !i.will_create_new && selectedItems[i.id]).length} existing parts will be updated
            </p>
            <p className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-gray-400" />
              {partItems.filter(i => !selectedItems[i.id]).length} parts will be skipped
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleApplyChanges}
            disabled={getSelectedCount() === 0}
          >
            Apply {getSelectedCount()} Changes
          </Button>
        </div>
      </div>
    );
  };

  const renderApplyingStep = () => (
    <div className="space-y-6 text-center py-12">
      <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin" />
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Applying Changes</h3>
        <p className="text-sm text-gray-600">
          Creating parts and updating records...
        </p>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6 text-center py-12">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Complete!</h3>
        <p className="text-sm text-gray-600">
          Your RFQ data has been successfully imported.
        </p>
      </div>
      <Button onClick={onClose}>Close</Button>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'upload':
        return renderUploadStep();
      case 'processing':
        return renderProcessingStep();
      case 'review':
        return renderReviewStep();
      case 'applying':
        return renderApplyingStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return renderUploadStep();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            RFQ Document Import
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload an RFQ document to extract and import data'}
            {step === 'processing' && 'Analyzing document...'}
            {step === 'review' && 'Review extracted data before importing'}
            {step === 'applying' && 'Applying changes...'}
            {step === 'complete' && 'Import completed'}
          </DialogDescription>
        </DialogHeader>
        {renderStep()}
      </DialogContent>
    </Dialog>
  );
};

export default RFQImportModal;
